import React, { useEffect, useState } from 'react';
import CustomLoading from '../../../util_components/custom_loading';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import TpDialog from '../shared/tp_dialog';
import { sort_rows, toggle_sort } from '../shared/use_sortable';
import WhyPopover from './why_popover';
import CodeBreaks from '../shared/code_breaks';
import SessionDateLink from '../shared/session_date_link';
import RuleRefLink from '../rules_engine/rule_ref';
import { fetch_drilldown } from '../api';
import { fmt, is_permission_error } from '../format';
import { Ledger } from '../shared/cells';
import {
  collect_session_statuses,
  group_session_rows,
  ist_time_text,
  own_rows,
  penalty_text,
  session_status_options
} from './drilldown_logic';
import { drilldown_view } from './adapters';
import { DRILLDOWN_CATEGORY_ADDITIONAL, drilldown_params } from './params';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import {
  additional_entry_csv_columns,
  drilldown_item_csv_columns,
  overview_csv_name
} from './csv_columns';

// Read-only drill-down of one category's item and penalty rows; edits happen on session details.
// Day and time break between, never inside, so a narrow column reads two lines.
const TimeText = ({ text }) =>
  text.split(', ').map((part, i) => (
    <React.Fragment key={part}>
      {i ? ', ' : ''}
      <span className="tp-nowrap">{part}</span>
    </React.Fragment>
  ));

const EMPTY_VIEW = { days: [], show_payment_col: true, count: 0 };

const SessionDrilldownModal = ({
  show,
  close,
  pay_month,
  month_label,
  teacher_uuid,
  category,
  category_label,
  kind,
  on_error
}) => {
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(EMPTY_VIEW);
  const [statuses, setStatuses] = useState([]);
  const [status_filter, setStatusFilter] = useState('');
  // A failed load is not an empty category: it shows its own error, and attempt drives Try again.
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [sort, setSort] = useState(null);
  const on_sort = (key) => setSort((s) => toggle_sort(s, key));

  // A new category/kind starts unfiltered so the status options cover the whole category.
  useEffect(() => {
    setStatusFilter('');
    setStatuses([]);
    setSort(null);
  }, [teacher_uuid, pay_month, category, kind]);

  useEffect(() => {
    if (!show || !category || !teacher_uuid) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    fetch_drilldown(
      drilldown_params({ teacher_uuid, pay_month, category, kind, session_status: status_filter })
    )
      .then((data) => {
        if (cancelled) return;
        const next = drilldown_view(data);
        setView(next);
        if (!status_filter) setStatuses(collect_session_statuses(next.days));
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        if (is_permission_error(e)) on_error(e, 'Failed to load the session drill-down.');
        setView(EMPTY_VIEW);
        setFailed(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, teacher_uuid, pay_month, category, kind, status_filter, attempt, on_error]);

  // The framing and the columns follow the request (known before, and without, a response).
  const entries = category === DRILLDOWN_CATEGORY_ADDITIONAL;
  const show_payment = view.show_payment_col;
  // This teacher's lines only, one row per session: pay line and penalties combined (amounts summed).
  // Additional entries are entries, not session lines: never grouped.
  const listed = view.days.flatMap((d) => d.rows);
  const flat_rows = entries ? listed : group_session_rows(own_rows(listed, teacher_uuid));

  // Earnings read neutral and penalties red; a zero or missing value is muted.
  const amount_cell = (value, tone, explain) =>
    value === null ? (
      <span className="tp-muted">—</span>
    ) : (
      <WhyPopover
        detail={explain}
        amount_node={<span className={Number(value) ? tone : 'tp-muted'}>{fmt(value, 2)}</span>}
      />
    );

  // A penalty's own breakdown from money.*; an orphan was never charged, so its amount reads apart.
  const penalty_cell = (r) => {
    if (r.is_orphaned) {
      return (
        <WhyPopover
          detail={r.explain}
          amount_node={
            <span className="tp-neg">
              Orphaned - not charged ({fmt(r.uncharged_orphan_amount, 2)})
            </span>
          }
        />
      );
    }
    const has_correction = r.corrected_amount !== null;
    return (
      <WhyPopover
        detail={r.explain}
        amount_node={
          <span>
            <span
              className="tp-neg"
              style={has_correction ? { textDecoration: 'line-through' } : undefined}
            >
              {penalty_text(r.original_amount)}
            </span>
            {has_correction ? (
              <>
                {' → '}
                <span className="tp-neg">{penalty_text(r.corrected_amount)}</span>
              </>
            ) : null}
            {r.waived_amount > 0 ? (
              <div className="tp-pos tp-sd-sub">Waived {fmt(r.waived_amount, 2)}</div>
            ) : null}
            {r.free_pass_amount > 0 ? (
              <div className="tp-pos tp-sd-sub">Free Pass {fmt(r.free_pass_amount, 2)}</div>
            ) : null}
          </span>
        }
      />
    );
  };

  // a combined session lists each of its penalties, each with its own breakdown
  const penalty_cells = (r) =>
    r.penalties && r.penalties.length ? (
      <span className="tp-sd-parts">
        {r.penalties.map((p) => (
          <span key={p.key} className="tp-sd-part">
            {penalty_cell(p)}
          </span>
        ))}
      </span>
    ) : (
      penalty_cell(r)
    );

  const card_money = (r) =>
    [
      show_payment && r.payment !== null
        ? { label: 'Payment', value: amount_cell(r.payment, 'tp-strong', r.explain) }
        : null,
      r.incentive !== null
        ? { label: 'Incentive', value: amount_cell(r.incentive, 'tp-strong', r.explain) }
        : null,
      r.kind === 'penalty' || (r.penalties && r.penalties.length)
        ? { label: 'Penalty', value: penalty_cells(r) }
        : null
    ].filter(Boolean);

  // An entry's related session reads by its name (student, class or workshop), not a uuid prefix.
  const session_link = (r) =>
    r.session_uuid ? (
      <SessionDateLink uuid={r.session_uuid} kind={r.session_kind}>
        {r.session_label || 'Open session'}
      </SessionDateLink>
    ) : (
      '—'
    );

  const additional_columns = [
    { key: 'date_text', label: 'Date / time (IST)', sort_key: 'date_text', sort_type: 'text' },
    {
      key: 'category_label',
      label: 'Category',
      sort_key: 'category_label',
      sort_type: 'text',
      hideInCard: true // the card title
    },
    { key: 'source_text', label: 'Source', sort_key: 'source_text', sort_type: 'text' },
    {
      key: 'reference_id',
      label: 'Ref',
      render: (r) => <span className="tp-mono">{r.reference_id || '—'}</span>
    },
    {
      key: 'session',
      label: 'Session',
      render: (r) => session_link(r)
    },
    {
      key: 'amount',
      label: 'Amount',
      align: 'right',
      sort_key: 'amount',
      sort_type: 'number',
      render: (r) => (
        <WhyPopover
          detail={r.explain}
          amount_node={<span className={r.amount_tone}>{r.amount_text}</span>}
        />
      )
    },
    {
      key: 'note',
      label: 'Note',
      render: (r) => (
        <span className="tp-sd-type" title={r.note || undefined}>
          {r.note || '—'}
        </span>
      )
    },
    {
      key: 'added_by',
      label: 'Added by',
      sort_key: 'added_by',
      sort_type: 'text',
      render: (r) => (
        <span className="tp-sd-sub" title={r.added_by_title || undefined}>
          {r.added_by}
        </span>
      )
    }
  ];

  const item_columns = [
    {
      key: 'time_label',
      label: 'Time',
      sort_key: 'time_label',
      sort_type: 'text',
      render: (r) => (
        <SessionDateLink uuid={r.session_uuid} kind={r.session_kind}>
          <TimeText text={ist_time_text(r.time_label)} />
        </SessionDateLink>
      ),
      hideInCard: true // the card title
    },
    // When the row does not fit: Duration folds under Status.
    {
      key: 'status_label',
      label: 'Status',
      sort_key: 'status_label',
      sort_type: 'text',
      // Codes stay upper case and break only after an underscore; 150px fits the common ones.
      min_width: 150,
      render: (r) => (
        <span className="tp-mono tp-code">
          <CodeBreaks text={r.status_label} />
        </span>
      )
    },
    {
      key: 'type_label',
      label: 'Type',
      sort_key: 'type_label',
      sort_type: 'text',
      // room for the session type and its rule link
      min_width: 160,
      render: (r) => (
        <span
          className="tp-sd-type"
          title={
            (r.parts || [r])
              .map((p) => p.note)
              .filter(Boolean)
              .join(' · ') || undefined
          }
        >
          {/* who/which class the session was, so Ops can tell sessions apart */}
          {r.session_label ? <span className="tp-sd-name">{r.session_label}</span> : null}
          {(r.parts || [r]).map((p) => (
            <span key={p.key} className={r.parts ? 'tp-sd-part' : undefined}>
              <CodeBreaks text={p.type_label} />
              {p.note ? ` — ${p.note}` : ''}
              <RuleRefLink rule_ref={p.rule_ref} on_error={on_error} />
            </span>
          ))}
        </span>
      )
    },
    { key: 'duration_label', label: 'Duration', fold: { into: 'status_label', order: 1 } },
    ...(show_payment
      ? [
          {
            key: 'payment',
            label: 'Payment',
            align: 'right',
            sort_key: 'payment',
            sort_type: 'number',
            hideInCard: true, // the money ledger below
            render: (r) => amount_cell(r.payment, 'tp-strong', r.explain)
          }
        ]
      : []),
    {
      key: 'incentive',
      label: 'Incentive',
      align: 'right',
      sort_key: 'incentive',
      sort_type: 'number',
      hideInCard: true, // the money ledger below
      render: (r) => amount_cell(r.incentive, 'tp-strong', r.explain)
    },
    {
      key: 'penalty',
      label: 'Penalty',
      align: 'right',
      sort_key: 'penalty',
      sort_type: 'number',
      hideInCard: true, // the money ledger below
      render: (r) =>
        r.kind === 'penalty' || (r.penalties && r.penalties.length)
          ? penalty_cells(r)
          : amount_cell(r.penalty, 'tp-neg', r.explain)
    },
    // Cards: the amounts as one right-aligned ledger (the twin of the three money columns).
    {
      key: 'money',
      label: 'Money',
      in_table: false,
      card_wide: true,
      card_render: (r) => <Ledger items={card_money(r)} />
    }
  ];

  const columns = entries ? additional_columns : item_columns;
  const rows = sort_rows(flat_rows, columns, sort);
  // The CSV keeps each line (the table groups a session's lines), same status filter and sort.
  const csv_rows = entries ? rows : sort_rows(own_rows(listed, teacher_uuid), columns, sort);
  const csv_columns = entries
    ? additional_entry_csv_columns(teacher_uuid)
    : drilldown_item_csv_columns(teacher_uuid, show_payment);

  if (!show) return null;

  return (
    <TpDialog title_id="tp-drilldown-title" on_close={close} size="lg">
      <div className="tp-modal tp-modal-wide tp-sd">
        <h4 id="tp-drilldown-title">
          {entries
            ? `Additional payments · ${month_label}`
            : `Session details — ${category_label || category} · ${month_label}`}
        </h4>
        <div className="tp-ctx">
          {entries
            ? 'Read-only. Entries are added, edited and removed on the Additional payments tab; the payment recalculates from there.'
            : 'Read-only. Session-level corrections are made on the Hridaya session details page; the payment recalculates from there.'}
        </div>

        <div className="tp-sd-bar">
          {entries ? null : (
            <>
              <label className="tp-select-k" htmlFor="tp-sd-status">
                Status
              </label>
              <CustomSingleSelect
                id="tp-sd-status"
                label="Status"
                noLabel
                ariaLabel="Session status"
                options={session_status_options(statuses)}
                value={status_filter}
                defaultOption="All statuses"
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{}}
              />
            </>
          )}
          {failed ? null : <span className="tp-card-hd-sub tp-sd-count">{rows.length} rows</span>}
          {loading || failed ? null : (
            <DownloadCsvButton
              className="tp-sd-csv"
              columns={csv_columns}
              rows={csv_rows}
              file_name={() =>
                overview_csv_name(
                  entries ? 'additional-payments-detail' : `sessions-${category}`,
                  pay_month
                )
              }
            />
          )}
        </div>

        {loading ? <CustomLoading /> : null}
        {!loading && failed ? (
          <LoadFailed
            text={entries ? 'Could not load the entries.' : 'Could not load the sessions.'}
            on_retry={() => setAttempt((a) => a + 1)}
          />
        ) : null}
        {!loading && !failed ? (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(r) => r.key}
            sort={sort}
            on_sort={on_sort}
            emptyText={
              entries
                ? 'No additional payments for this teacher and month.'
                : 'No rows for this category and status.'
            }
            cardTitle={(r) =>
              entries ? (
                r.category_label
              ) : (
                <SessionDateLink uuid={r.session_uuid} kind={r.session_kind}>
                  {ist_time_text(r.time_label)}
                </SessionDateLink>
              )
            }
          />
        ) : null}

        <div className="tp-modal-ft">
          <button type="button" className="tp-btn-ghost" onClick={close}>
            Close
          </button>
        </div>
      </div>
    </TpDialog>
  );
};

export default SessionDrilldownModal;
